/* IdChipThemeAdminPage — Iter211 (extended Iter212)
 *
 * Office-admin page to customise the colour scheme, font and visual
 * effects of the four ID chips (Client / Property / Appraisal / Lead)
 * used across the CRM. Each row has a live preview, colour pickers,
 * font controls (family / weight / style / size), and effect controls
 * (border radius/width/style, text transform, letter spacing, box +
 * text shadow presets). Resets to seeded defaults on demand. Saves to
 * the id_chip_theme singleton; every IdChip in the app picks up the
 * new theme via IdChipThemeContext refresh().
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Save, RotateCcw, Palette, Type, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIdChipTheme, ID_THEME_FALLBACK } from '@/contexts/IdChipThemeContext';
import IdChip from '@/components/IdChip';
import { toast } from 'sonner';

const PREVIEW_VALUES = { client: '2536', property: '3110', appraisal: '5004', lead: '42' };

const FONT_FAMILIES = [
  { label: 'Monospace (default)', value: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
  { label: 'Sans (Inter / Manrope)', value: 'Manrope, Inter, ui-sans-serif, system-ui, sans-serif' },
  { label: 'Sans (system)', value: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif' },
  { label: 'Serif (Georgia)', value: 'Georgia, Cambria, "Times New Roman", Times, serif' },
  { label: 'Rounded (Nunito)', value: 'Nunito, ui-rounded, system-ui, sans-serif' },
  { label: 'Condensed', value: '"Roboto Condensed", "Arial Narrow", sans-serif' },
];

const FONT_WEIGHTS = [
  { label: 'Normal (400)', value: '400' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Semibold (600)', value: '600' },
  { label: 'Bold (700)', value: '700' },
  { label: 'Extra bold (800)', value: '800' },
  { label: 'Black (900)', value: '900' },
];

const FONT_STYLES = [
  { label: 'Upright', value: 'normal' },
  { label: 'Italic', value: 'italic' },
];

const FONT_SIZES = [
  { label: 'XS (12px)', value: '0.75rem' },
  { label: 'SM (13px)', value: '0.8125rem' },
  { label: 'Base (14px)', value: '0.875rem' },
  { label: 'MD (15px)', value: '0.9375rem' },
  { label: 'LG (16px)', value: '1rem' },
  { label: 'XL (18px)', value: '1.125rem' },
];

const TEXT_TRANSFORMS = [
  { label: 'As typed', value: 'none' },
  { label: 'UPPERCASE', value: 'uppercase' },
  { label: 'lowercase', value: 'lowercase' },
  { label: 'Capitalize', value: 'capitalize' },
];

const LETTER_SPACINGS = [
  { label: 'Tight (-0.5px)', value: '-0.025em' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide (+0.5px)', value: '0.025em' },
  { label: 'Wider (+1px)', value: '0.05em' },
];

const BORDER_RADII = [
  { label: 'Square', value: '0px' },
  { label: 'Small (4px)', value: '0.25rem' },
  { label: 'Medium (6px)', value: '0.375rem' },
  { label: 'Large (8px)', value: '0.5rem' },
  { label: 'Pill', value: '9999px' },
];

const BORDER_WIDTHS = [
  { label: 'None', value: '0px' },
  { label: '1px', value: '1px' },
  { label: '2px', value: '2px' },
  { label: '3px', value: '3px' },
];

const BORDER_STYLES = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
  { label: 'Double', value: 'double' },
];

const BOX_SHADOWS = [
  { label: 'None', value: 'none' },
  { label: 'Subtle', value: '0 1px 2px rgba(15,23,42,0.08)' },
  { label: 'Soft', value: '0 2px 6px rgba(15,23,42,0.12)' },
  { label: 'Lifted', value: '0 4px 12px rgba(15,23,42,0.18)' },
  { label: 'Inner', value: 'inset 0 1px 2px rgba(15,23,42,0.18)' },
];

const TEXT_SHADOWS = [
  { label: 'None', value: 'none' },
  { label: 'Soft glow', value: '0 0 6px rgba(255,255,255,0.6)' },
  { label: 'Drop', value: '0 1px 0 rgba(15,23,42,0.25)' },
  { label: 'Embossed', value: '0 1px 0 rgba(255,255,255,0.5)' },
];

const PADDING_X_OPTIONS = [
  { label: 'Tight (4px)', value: '0.25rem' },
  { label: 'Default (6px)', value: '0.375rem' },
  { label: 'Roomy (8px)', value: '0.5rem' },
  { label: 'Wide (12px)', value: '0.75rem' },
];

const PADDING_Y_OPTIONS = [
  { label: 'Hairline (1px)', value: '0.0625rem' },
  { label: 'Thin (2px)', value: '0.125rem' },
  { label: 'Default (4px)', value: '0.25rem' },
  { label: 'Tall (6px)', value: '0.375rem' },
];

function Select({ value, onChange, options, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded bg-white"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function ColourInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="color"
        data-testid={`${testId}-colour`}
        value={value}
        onChange={e => onChange(e.target.value.toUpperCase())}
        className="w-8 h-7 rounded border border-slate-300 cursor-pointer p-0"
      />
      <input
        type="text"
        data-testid={`${testId}-hex`}
        value={value}
        onChange={e => onChange(e.target.value.toUpperCase())}
        maxLength={7}
        className="flex-1 min-w-0 px-1.5 py-1 text-xs font-mono-num border border-slate-300 rounded uppercase"
      />
    </div>
  );
}

export default function IdChipThemeAdminPage() {
  const { api } = useAuth();
  const { refresh } = useIdChipTheme();
  const [form, setForm] = useState(ID_THEME_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!api) return;
    api.get('/id-chip-theme').then(({ data }) => {
      if (data && typeof data === 'object') {
        const merged = {};
        ['client', 'property', 'appraisal', 'lead'].forEach(t => {
          merged[t] = { ...ID_THEME_FALLBACK[t], ...(data[t] || {}) };
        });
        setForm(merged);
      }
    }).catch(() => { /* fallback */ }).finally(() => setLoading(false));
  }, [api]);

  const setField = (type, field, value) => {
    setForm(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const resetType = (type) => setForm(prev => ({ ...prev, [type]: ID_THEME_FALLBACK[type] }));
  const resetAll  = () => setForm(ID_THEME_FALLBACK);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/id-chip-theme', form);
      await refresh();
      toast.success('ID chip theme saved — every chip across the app picks up the new look.');
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="id-chip-theme-admin-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
            <Palette size={24} className="text-[#D35400]" /> ID Chip Theme
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
            Brand the four ID chips (Client / Property / Appraisal / Lead) that appear everywhere across the CRM. Tweak colour, font and visual effects — live preview on the left. Saved changes apply instantly to every chip in the app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="id-chip-theme-reset-all"
            onClick={resetAll}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm font-medium"
          >
            <RotateCcw size={14} /> Reset all
          </button>
          <button
            data-testid="id-chip-theme-save"
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-3 py-2 bg-[#D35400] hover:bg-[#B84A00] text-white rounded-md text-sm font-bold disabled:opacity-40"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading current theme…
        </div>
      ) : (
        <div className="space-y-4">
          {['client', 'property', 'appraisal', 'lead'].map(type => {
            const pal = form[type];
            // Push form snapshot into context-shaped preview by overriding the component
            const previewProps = {
              backgroundColor: pal.bg,
              borderColor:     pal.border,
              color:           pal.text,
              fontFamily:      pal.fontFamily,
              fontWeight:      pal.fontWeight,
              fontStyle:       pal.fontStyle,
              letterSpacing:   pal.letterSpacing,
              textTransform:   pal.textTransform,
              borderRadius:    pal.borderRadius,
              borderWidth:     pal.borderWidth,
              borderStyle:     pal.borderStyle,
              boxShadow:       pal.boxShadow,
              textShadow:      pal.textShadow,
              paddingLeft:     pal.paddingX,
              paddingRight:    pal.paddingX,
              paddingTop:      pal.paddingY,
              paddingBottom:   pal.paddingY,
              display:         'inline-flex',
              alignItems:      'center',
              lineHeight:      1.15,
            };
            const prefix = { client: 'C', property: 'P', appraisal: 'A', lead: 'L' }[type];

            return (
              <div key={type} data-testid={`id-chip-theme-row-${type}`} className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-bold text-slate-900 capitalize">{type}</h2>
                    <div className="flex items-center gap-2">
                      <span style={{ ...previewProps, fontSize: pal.fontSize }}>{prefix}{PREVIEW_VALUES[type]}</span>
                      <span style={{ ...previewProps, fontSize: `calc(${pal.fontSize} * 1.15)` }}>{prefix}{PREVIEW_VALUES[type]}</span>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">← live preview</span>
                    </div>
                  </div>
                  <button
                    data-testid={`id-chip-theme-${type}-reset`}
                    onClick={() => resetType(type)}
                    className="text-[10px] text-slate-400 hover:text-slate-700 underline whitespace-nowrap"
                  >
                    ↻ reset {type}
                  </button>
                </div>

                {/* Colours */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                    <Palette size={11} /> Colour
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {['bg', 'border', 'text'].map(field => (
                      <label key={field} className="flex flex-col gap-1">
                        <span className="text-[10px] text-slate-500 font-semibold capitalize">{field}</span>
                        <ColourInput
                          value={pal[field]}
                          onChange={v => setField(type, field, v)}
                          testId={`id-chip-theme-${type}-${field}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                {/* Font */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                    <Type size={11} /> Font
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Family</span>
                      <Select testId={`id-chip-theme-${type}-fontFamily`} value={pal.fontFamily} onChange={v => setField(type, 'fontFamily', v)} options={FONT_FAMILIES} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Weight</span>
                      <Select testId={`id-chip-theme-${type}-fontWeight`} value={pal.fontWeight} onChange={v => setField(type, 'fontWeight', v)} options={FONT_WEIGHTS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Style</span>
                      <Select testId={`id-chip-theme-${type}-fontStyle`} value={pal.fontStyle} onChange={v => setField(type, 'fontStyle', v)} options={FONT_STYLES} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Size</span>
                      <Select testId={`id-chip-theme-${type}-fontSize`} value={pal.fontSize} onChange={v => setField(type, 'fontSize', v)} options={FONT_SIZES} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Transform</span>
                      <Select testId={`id-chip-theme-${type}-textTransform`} value={pal.textTransform} onChange={v => setField(type, 'textTransform', v)} options={TEXT_TRANSFORMS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Letter spacing</span>
                      <Select testId={`id-chip-theme-${type}-letterSpacing`} value={pal.letterSpacing} onChange={v => setField(type, 'letterSpacing', v)} options={LETTER_SPACINGS} />
                    </label>
                  </div>
                </div>

                {/* Effects */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                    <Sparkles size={11} /> Effects
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Corner radius</span>
                      <Select testId={`id-chip-theme-${type}-borderRadius`} value={pal.borderRadius} onChange={v => setField(type, 'borderRadius', v)} options={BORDER_RADII} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Border width</span>
                      <Select testId={`id-chip-theme-${type}-borderWidth`} value={pal.borderWidth} onChange={v => setField(type, 'borderWidth', v)} options={BORDER_WIDTHS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Border style</span>
                      <Select testId={`id-chip-theme-${type}-borderStyle`} value={pal.borderStyle} onChange={v => setField(type, 'borderStyle', v)} options={BORDER_STYLES} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Box shadow</span>
                      <Select testId={`id-chip-theme-${type}-boxShadow`} value={pal.boxShadow} onChange={v => setField(type, 'boxShadow', v)} options={BOX_SHADOWS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Text shadow</span>
                      <Select testId={`id-chip-theme-${type}-textShadow`} value={pal.textShadow} onChange={v => setField(type, 'textShadow', v)} options={TEXT_SHADOWS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Padding X</span>
                      <Select testId={`id-chip-theme-${type}-paddingX`} value={pal.paddingX} onChange={v => setField(type, 'paddingX', v)} options={PADDING_X_OPTIONS} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] text-slate-500 font-semibold">Padding Y</span>
                      <Select testId={`id-chip-theme-${type}-paddingY`} value={pal.paddingY} onChange={v => setField(type, 'paddingY', v)} options={PADDING_Y_OPTIONS} />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}

          {/* In-context preview row using the live IdChip component (reads saved theme) */}
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Currently saved theme (reads context — refreshes after Save)</div>
            <div className="flex flex-wrap gap-3 items-center">
              <IdChip type="client" value="2536" size="md" />
              <IdChip type="property" value="3110" size="md" />
              <IdChip type="appraisal" value="5004" size="md" />
              <IdChip type="lead" value="42" size="md" />
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400 italic">
        Tip — keep enough contrast between text and background. Chips are small, so subtle effects (soft shadow, tight letter-spacing) read better than heavy ones at this scale.
      </p>
    </div>
  );
}
